import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { Attribute } from '../models/attribute';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class AttributeDefinitionsService extends CrudService implements AdvancedService<Attribute> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    /**
     * Méthode pour alimenter le tableau de la liste des attributs
     */
    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<Attribute>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/attributedefinitions`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<Attribute>(Attribute, x);

                // Filtre pour ne pas afficher les attributs ViewId et BoxId
                res.value = res.value.filter((a) => a.name !== 'ViewId' && a.name !== 'BoxId');

                return res;
            })
        );
    }

    /**
     *
     * @param context Retourne la liste les attributs système.
     * @param filters
     * @param generalFilters
     * @param sortInfos
     * @param pageNumber
     * @param pageSize
     * @returns Liste des attributs système.
     */
    SystemList(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<Attribute>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/attributedefinitions`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<Attribute>(Attribute, x);
                return res;
            })
        );
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http
            .get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/attributedefinitions/export`, {
                headers: headers,
                responseType: 'blob',
            })
            .pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
    }

    update(attributeId: number, attribute: Attribute): Observable<Attribute> {
        // Appel de la Web Api
        return this.http
            .put<Attribute>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/attributedefinitions/${attributeId}`,
                attribute,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    delete(attributeId: number): Observable<boolean> {
        // Appel de la Web Api
        return this.http
            .delete<Attribute>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/attributedefinitions/${attributeId}`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    add(attribute: Attribute): Observable<Attribute> {
        attribute.organizationId = this.organizationId;

        // Appel de la Web Api
        return this.http
            .post<Attribute>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/attributedefinitions`, attribute, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    get(attributeId: number): Observable<Attribute> {
        // Appel de la Web Api
        return this.http
            .get<Attribute>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/attributedefinitions/${attributeId}`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    applyOrderChanges(attributes: Attribute[]) {
        // Appel de la Web Api
        return this.http
            .patch<Attribute[]>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/attributedefinitions/`, attributes, this.getDefaultHttpOptions())
            .pipe(map((x) => {}));
    }
}
